import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button, Empty, Input, Select, Spin, Table, Tabs, message } from "antd";
import {
  DownloadOutlined,
  EditOutlined,
  ReloadOutlined,
  SendOutlined,
  RightOutlined,
  SearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  getInstrument,
  listInstrumentResponses,
  listResponseConversations
} from "pages/VoiceCaptureManagement/feedbackService";
import { QUESTION_TYPE } from "pages/SurveyManagement/constants";
import ResponseDetailDrawer from "./ResponseDetailDrawer";
import ResponsesSummary from "./ResponsesSummary";
import {
  answerText,
  buildExportTable,
  buildQuestionSummaries,
  companyOptions,
  exportFileName,
  filterResponses,
  formatDuration,
  isCallResponse,
  pickColumnQuestions,
  respondentEmail,
  respondentName,
  responseCounts,
  responseNumber
} from "./responseFormat";
import styles from "./style.module.scss";

const PAGE_SIZE = 20;
const CHOICE_TYPES = [
  QUESTION_TYPE.SINGLE_CHOICE,
  QUESTION_TYPE.MULTIPLE_CHOICE,
  QUESTION_TYPE.RATING
];

// Opened responses, per survey, in this browser only: the dot marks the ones not opened yet.
const seenKey = instrumentId => `pivony_survey_seen_${instrumentId}`;

function readSeen(instrumentId) {
  try {
    const raw = window.localStorage.getItem(seenKey(instrumentId));
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids : []);
  } catch (_) {
    return new Set();
  }
}

function writeSeen(instrumentId, seen) {
  try {
    window.localStorage.setItem(
      seenKey(instrumentId),
      JSON.stringify([...seen])
    );
  } catch (_) {
    // Private mode or full storage: the dots just stay.
  }
}

// The survey list opens the survey's editor from ?edit=<id>, on Distribute with &step=distribute.
const editPath = (instrumentId, step) =>
  `/console/sonic-survey?edit=${instrumentId}${step ? `&step=${step}` : ""}`;

function downloadExcel(headers, rows, filename) {
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Responses");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const ResponsesPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [instrument, setInstrument] = useState(null);
  const [responses, setResponses] = useState([]);
  const [tab, setTab] = useState("responses");
  const [view, setView] = useState("completed");
  const [company, setCompany] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [seen, setSeen] = useState(() => readSeen(id));
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getInstrument(id), listInstrumentResponses(id)])
      .then(([inst, items]) => {
        setInstrument(inst);
        setResponses(items || []);
      })
      .catch(() => message.error(t("feedbackCapture.responses_load_error")))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    load();
    setSeen(readSeen(id));
  }, [id, load]);

  const questions = useMemo(() => instrument?.config?.questions || [], [
    instrument
  ]);
  const counts = useMemo(() => responseCounts(responses), [responses]);
  const companies = useMemo(() => companyOptions(responses), [responses]);
  const visible = useMemo(
    () => filterResponses(responses, { view, company, search }),
    [responses, view, company, search]
  );
  const summaries = useMemo(
    () => buildQuestionSummaries(questions, responses),
    [questions, responses]
  );
  const columnQuestions = useMemo(() => pickColumnQuestions(questions), [
    questions
  ]);
  const hasContact = useMemo(() => responses.some(r => r.contact), [responses]);

  const openIndex = visible.findIndex(r => r.id === openId);
  const openResponse = openIndex >= 0 ? visible[openIndex] : null;

  const openRow = response => {
    setOpenId(response.id);
    if (!seen.has(response.id)) {
      const next = new Set(seen);
      next.add(response.id);
      setSeen(next);
      writeSeen(id, next);
    }
  };

  const step = delta => {
    const next = visible[openIndex + delta];
    if (next) openRow(next);
  };

  // Phone responses bring their call transcripts; without them the file still downloads.
  const exportVisible = async () => {
    let conversations = {};
    if (visible.some(isCallResponse)) {
      setExporting(true);
      try {
        conversations = await listResponseConversations(id);
      } catch (_) {
        message.warning(
          t("feedbackCapture.responsesPage.export_transcripts_failed")
        );
      }
      setExporting(false);
    }
    const { headers, rows } = buildExportTable(
      questions,
      visible,
      responses,
      t,
      conversations
    );
    downloadExcel(headers, rows, exportFileName(instrument?.name));
  };

  const columns = [
    ...(hasContact
      ? [
          {
            title: t("feedbackCapture.responsesPage.col_name"),
            key: "name",
            render: (_, r) => (
              <span className={styles.nameCell}>
                {!seen.has(r.id) ? (
                  <span
                    className={styles.newDot}
                    aria-label={t("feedbackCapture.responsesPage.new")}
                  />
                ) : null}
                {respondentName(r) || "—"}
              </span>
            )
          },
          {
            title: t("feedbackCapture.responsesPage.col_email"),
            key: "email",
            render: (_, r) => respondentEmail(r) || "—"
          }
        ]
      : [
          {
            title: t("feedbackCapture.responsesPage.col_response"),
            key: "number",
            render: (_, r) => (
              <span className={styles.nameCell}>
                {!seen.has(r.id) ? <span className={styles.newDot} /> : null}#
                {responseNumber(r, responses)}
              </span>
            )
          }
        ]),
    ...columnQuestions.map(q => ({
      title: (
        <span className={styles.colTitle} title={q.text}>
          {q.text}
        </span>
      ),
      key: `q_${q.id}`,
      render: (_, r) => {
        const value = answerText(r, q.id);
        if (!value) return <span className={styles.muted}>—</span>;
        return CHOICE_TYPES.includes(q.type) ? (
          <span className={styles.pill}>{value}</span>
        ) : (
          <span className={styles.snippet} title={value}>
            {value}
          </span>
        );
      }
    })),
    ...(!columnQuestions.length && responses.some(r => r.text)
      ? [
          {
            title: t("feedbackCapture.response_text_capture"),
            key: "text",
            render: (_, r) => (
              <span className={styles.snippet}>{r.text || "—"}</span>
            )
          }
        ]
      : []),
    {
      title: t("feedbackCapture.responsesPage.col_submitted"),
      key: "submitted",
      width: 150,
      render: (_, r) =>
        r.completed_at ? dayjs(r.completed_at).format("D MMM, HH:mm") : "—"
    },
    {
      key: "open",
      width: 36,
      render: () => <RightOutlined className={styles.muted} />
    }
  ];

  const average = formatDuration(counts.averageSeconds, t);
  const title =
    instrument?.name || t("feedbackCapture.responses_drawer_title_fallback");

  return (
    <div className={styles.page}>
      <div className={styles.crumb}>
        <Link to="/console/sonic-survey">
          {t("feedbackCapture.unified_title")}
        </Link>
        <span> / </span>
        <span>{t("feedbackCapture.responses_drawer_title_fallback")}</span>
      </div>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          <div className={styles.stats}>
            <span>
              {t("feedbackCapture.responsesPage.stat_completed", {
                count: counts.completed
              })}
            </span>
            <span>
              {t("feedbackCapture.responsesPage.stat_incomplete", {
                count: counts.incomplete
              })}
            </span>
            {counts.latestAt ? (
              <span>
                {t("feedbackCapture.responsesPage.stat_latest", {
                  date: dayjs(counts.latestAt).format("D MMM, HH:mm")
                })}
              </span>
            ) : null}
            {average ? (
              <span>
                {t("feedbackCapture.responsesPage.stat_average", {
                  duration: average
                })}
              </span>
            ) : null}
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button icon={<EditOutlined />} onClick={() => history.push(editPath(id))}>
            {t("surveyManagement.action_edit")}
          </Button>
          <Button icon={<SendOutlined />} onClick={() => history.push(editPath(id, "distribute"))}>
            {t("surveyManagement.step_distribute")}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
            aria-label={t("surveyManagement.refresh")}
          />
        </div>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        className={styles.tabs}
        items={[
          {
            key: "responses",
            label: t("feedbackCapture.responsesPage.tab_responses")
          },
          {
            key: "summary",
            label: t("feedbackCapture.responsesPage.tab_summary")
          }
        ]}
      />

      {loading && !instrument ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : (
        <Spin spinning={loading}>
          {tab === "responses" ? (
            <>
              <div className={styles.toolbar}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t(
                    "feedbackCapture.responsesPage.search_placeholder"
                  )}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={styles.search}
                />
                <Select
                  value={view}
                  onChange={setView}
                  className={styles.select}
                  options={[
                    {
                      value: "completed",
                      label: `${t(
                        "feedbackCapture.responses_filter_completed"
                      )} (${counts.completed})`
                    },
                    {
                      value: "empty",
                      label: `${t("feedbackCapture.responses_filter_empty")} (${
                        counts.incomplete
                      })`
                    }
                  ]}
                />
                {companies.length ? (
                  <Select
                    value={company}
                    onChange={setCompany}
                    className={styles.select}
                    options={[
                      {
                        value: "all",
                        label: t("feedbackCapture.response_company_filter_all")
                      },
                      ...companies.map(({ domain, count }) => ({
                        value: domain,
                        label: t(
                          "feedbackCapture.response_company_filter_option",
                          { domain, count }
                        )
                      }))
                    ]}
                  />
                ) : null}
                <div className={styles.spacer} />
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={exportVisible}
                  loading={exporting}
                  disabled={!visible.length}
                >
                  {t("feedbackCapture.responsesPage.export_excel")}
                </Button>
              </div>
              {!loading && !visible.length ? (
                <Empty description={t("feedbackCapture.responses_empty")} />
              ) : (
                <Table
                  rowKey="id"
                  className={styles.table}
                  columns={columns}
                  dataSource={visible}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  pagination={
                    visible.length > PAGE_SIZE
                      ? {
                          pageSize: PAGE_SIZE,
                          showSizeChanger: false,
                          showTotal: (total, range) =>
                            t("feedbackCapture.responsesPage.showing", {
                              from: range[0],
                              to: range[1],
                              total
                            })
                        }
                      : false
                  }
                  onRow={record => ({
                    onClick: () => openRow(record),
                    className: [
                      styles.row,
                      record.id === openId ? styles.rowOpen : ""
                    ]
                      .filter(Boolean)
                      .join(" ")
                  })}
                />
              )}
            </>
          ) : (
            <ResponsesSummary counts={counts} summaries={summaries} />
          )}
        </Spin>
      )}

      <ResponseDetailDrawer
        open={!!openResponse}
        response={openResponse}
        position={openIndex + 1}
        total={visible.length}
        allResponses={responses}
        questions={questions}
        surveyTitle={instrument?.name}
        instrumentId={id}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
};

export default ResponsesPage;
